import React, { useEffect, useState } from 'react';
import { CornerDownRightIcon } from 'lucide-react';
import { ManualLineInput } from '../../types/commission';
import { Button } from '../sf/Button';
import { Modal } from '../sf/Modal';
import { NewBadge } from '../sf/NewBadge';
import { ScopedNotification } from '../sf/ScopedNotification';
export type ManualLineMode = 'manual' | 'adjustment';
interface ManualLineModalProps {
  open: boolean;
  mode: ManualLineMode;
  context: string;
  payees: string[];
  defaultPayee: string;
  postsTo: string;
  lockedPeriods: string[];
  onClose: () => void;
  onSubmit: (input: ManualLineInput) => void;
}
const fieldClass = 'w-full rounded border border-sf-border-strong bg-white px-3 text-[13px] text-sf-text focus:border-sf-blue focus:outline-none focus:ring-1 focus:ring-sf-blue';
const errorClass = 'border-sf-red focus:border-sf-red focus:ring-sf-red';
export function ManualLineModal({
  open,
  mode,
  context,
  payees,
  defaultPayee,
  postsTo,
  lockedPeriods,
  onClose,
  onSubmit
}: ManualLineModalProps) {
  const [payee, setPayee] = useState(defaultPayee);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [splitPct, setSplitPct] = useState('');
  const [sourcePeriod, setSourcePeriod] = useState(lockedPeriods[0] ?? '');
  const [submitted, setSubmitted] = useState(false);
  const isAdjustment = mode === 'adjustment';
  useEffect(() => {
    if (!open) return;
    setPayee(defaultPayee);
    setAmount('');
    setReason('');
    setSplitPct('');
    setSourcePeriod(lockedPeriods[0] ?? '');
    setSubmitted(false);
  }, [open, defaultPayee, lockedPeriods]);
  const parsedAmount = Number(amount.replace(/[$,\s]/g, ''));
  const parsedSplit = splitPct.trim() === '' ? null : Number(splitPct);
  const errors = {
    amount: !amount.trim() || Number.isNaN(parsedAmount) || parsedAmount === 0 ? 'Enter a non-zero amount (use − for a deduction).' : '',
    reason: reason.trim() ? '' : 'A reason is required.',
    split: parsedSplit !== null && (Number.isNaN(parsedSplit) || parsedSplit <= 0 || parsedSplit >= 100) ? 'Split must be between 1 and 99.' : ''
  };
  const hasErrors = Boolean(errors.amount || errors.reason || errors.split);
  function handleSubmit() {
    setSubmitted(true);
    if (hasErrors) return;
    onSubmit({
      payee,
      amount: Math.round(parsedAmount * 100) / 100,
      reason: reason.trim(),
      splitPct: isAdjustment ? null : parsedSplit,
      sourcePeriod: isAdjustment ? sourcePeriod : null
    });
  }
  return <Modal open={open} onClose={onClose} title={isAdjustment ? 'Add Adjustment' : 'Add Manual Line'} description={context} footer={<>
          <Button onClick={onClose} data-id="element-14" data-mp="3ddqi65m2do0j">Cancel</Button>
          <Button variant="brand" onClick={handleSubmit} data-id="element-15" data-mp="3dack39kfo0yw">
            {isAdjustment ? 'Add Adjustment' : 'Add Line'}
          </Button>
        </>} data-id="element-13" data-mp="34ciwcn0qifnc">
      <div className="space-y-4" data-id="element-16" data-mp="sl56k7l91jkz">
        {isAdjustment && <>
            <ScopedNotification tone="info" icon={CornerDownRightIcon} data-id="element-17" data-mp="2d4qvg5jr2rgf">
              <span className="mb-1 flex" data-id="element-18" data-mp="2935lot2iitag">
                <NewBadge data-id="element-19" data-mp="2946x4ynfxhsp" />
              </span>
              For changes that affect a locked period. Posts to <strong data-id="element-20" data-mp="24avz9n0lwbcx">{postsTo}</strong>, the next open period — the locked period is never edited. Today a
              post-funding loan edit silently rewrites the original month’s fee.
            </ScopedNotification>
            <div data-id="element-21" data-mp="1ufatddsf2qxw">
              <label htmlFor="adj-source" className="mb-1 block text-xs font-semibold text-sf-text" data-id="element-22" data-mp="1ubmzuemz24j6">
                Affected locked period
              </label>
              <select id="adj-source" value={sourcePeriod} onChange={event => setSourcePeriod(event.target.value)} className={`${fieldClass} h-8`} data-id="element-23" data-mp="1le4c32hy1c3l">
                {lockedPeriods.map(period => <option key={period} value={period} data-id="element-24" data-mp="3fqu3ndkua97t">
                    {period} (Locked)
                  </option>)}
              </select>
            </div>
          </>}

        {!isAdjustment && <p className="flex flex-wrap items-center gap-2 text-xs text-sf-weak" data-id="element-25" data-mp="27fitomzbe9qp">
            <NewBadge data-id="element-26" data-mp="2cfna6d0834gx" />
            Manual lines with a required reason, and splits, are proposed rebuild features.
          </p>}

        <div className="grid grid-cols-[minmax(0,1fr)_160px] gap-3" data-id="element-27" data-mp="23fi549ii4i5x">
          <div data-id="element-28" data-mp="23huu36mofssh">
            <label htmlFor="ml-payee" className="mb-1 block text-xs font-semibold text-sf-text" data-id="element-29" data-mp="1oqud2d7v6qkq">
              Payee
            </label>
            <select id="ml-payee" value={payee} onChange={event => setPayee(event.target.value)} className={`${fieldClass} h-8`} data-id="element-30" data-mp="1tim3mnr0swav">
              {payees.map(name => <option key={name} value={name} data-id="element-31" data-mp="1jrhnhkplz5eg">
                  {name}
                </option>)}
            </select>
          </div>
          <div data-id="element-32" data-mp="15si0silnp0kx">
            <label htmlFor="ml-amount" className="mb-1 block text-xs font-semibold text-sf-text" data-id="element-33" data-mp="15u2uy7426hi9">
              <abbr title="required" className="mr-0.5 text-sf-red no-underline" data-id="element-34" data-mp="1at5oauaoezae">*</abbr>Amount (USD)
            </label>
            <input id="ml-amount" inputMode="decimal" placeholder="0.00" value={amount} onChange={event => setAmount(event.target.value)} aria-invalid={submitted && Boolean(errors.amount)} className={`${fieldClass} h-8 text-right tabular-nums ${submitted && errors.amount ? errorClass : ''}`} data-id="element-35" data-mp="m67u5j22z8qq" />
          </div>
        </div>
        {submitted && errors.amount && <p className="-mt-2 text-xs text-sf-red" data-id="element-36" data-mp="154sg6wp47nxi">{errors.amount}</p>}

        {!isAdjustment && <div data-id="element-37" data-mp="1jt6qttsrwgom">
            <label htmlFor="ml-split" className="mb-1 block text-xs font-semibold text-sf-text" data-id="element-38" data-mp="1jvszxxvbb48y">
              Split % <span className="font-normal text-sf-weak" data-id="element-39" data-mp="1ex80i0nvwolk">(optional)</span>
            </label>
            <div className="flex items-center gap-2" data-id="element-40" data-mp="1ssabv3bi365r">
              <input id="ml-split" inputMode="numeric" placeholder="e.g. 40" value={splitPct} onChange={event => setSplitPct(event.target.value)} className={`${fieldClass} h-8 w-24 text-right tabular-nums ${submitted && errors.split ? errorClass : ''}`} data-id="element-41" data-mp="1ou2ymig8wmzb" />
              <span className="text-xs text-sf-weak" data-id="element-42" data-mp="2akvwuvpai29k">This payee’s share. For a 60 / 40 split, add one line per LO.</span>
            </div>
            {submitted && errors.split && <p className="mt-1 text-xs text-sf-red" data-id="element-43" data-mp="2eott8yn8cyyx">{errors.split}</p>}
          </div>}

        <div data-id="element-44" data-mp="2jnwyok74h77f">
          <label htmlFor="ml-reason" className="mb-1 block text-xs font-semibold text-sf-text" data-id="element-45" data-mp="2okx6p0nj8k16">
            <abbr title="required" className="mr-0.5 text-sf-red no-underline" data-id="element-46" data-mp="2oj2h391bg3qr">*</abbr>Reason
          </label>
          <textarea id="ml-reason" rows={3} value={reason} onChange={event => setReason(event.target.value)} placeholder="Shown on the payee’s dashboard and in the audit trail" aria-invalid={submitted && Boolean(errors.reason)} className={`${fieldClass} py-2 ${submitted && errors.reason ? errorClass : ''}`} data-id="element-47" data-mp="u6w01tlqqsqj" />
          {submitted && errors.reason && <p className="mt-1 text-xs text-sf-red" data-id="element-48" data-mp="33ozvhr9t4v7z">{errors.reason}</p>}
        </div>
      </div>
    </Modal>;
}